import { usFillProgress, usOrderNeedsFillVisibility } from './fillProgress';
/**
 * #989 — pure presentation rules for Polymarket US orders and positions.
 *
 * No React, no store, no network. Every rule here mirrors a server rule in the
 * inactive relay foundation (#987), so the screen never offers an action the
 * relay's recorded state already rules out (the relay still re-checks live
 * evidence and may refuse), and never claims more than the evidence shows:
 *
 * - An order is ONE row. Ordered, filled and remaining come from the venue
 *   lifecycle; a held position is a separate row, never added to the order.
 * - Unknown evidence renders as unavailable, never as zero.
 * - A cancel acknowledgement is not a canceled order. Only the lifecycle says
 *   an order is canceled, and a fill that won the race stays filled.
 * - A late result is applied only to the exact scope and row it was asked for.
 */
import type {
  UsCancelAttempt,
  UsCancelResult,
  UsLifecycleState,
  UsOrderRow,
  UsPortfolioSnapshot,
  UsPositionRow,
  UsReadiness,
  UsReceipt,
  UsScope,
} from './types';
import { formatUsUsd } from './format';
import { usReason } from './reason';

/** Mirrors `US_CANCEL_RECOVERY_WAIT_MS` in relay/src/polymarketUs/cancellation.ts. */
export const US_CANCEL_REPEAT_WAIT_MS = 10_000;

export const UNAVAILABLE = 'Unavailable';

// ── Identity ───────────────────────────────────────────────────────────────

/** Row identity: scope plus G1's immutable intent id. Never a label or index. */
export function usOrderKey(row: Pick<UsOrderRow, 'scope' | 'intentId'>): string {
  return `${row.scope.venue}\u0000${row.scope.scopeKey}\u0000${row.intentId}`;
}

/** Position identity: scope plus the exact market and outcome. */
export function usPositionKey(row: Pick<UsPositionRow, 'scope' | 'target'>): string {
  return `${row.scope.venue}\u0000${row.scope.scopeKey}\u0000${row.target.marketSlug}\u0000${row.target.outcome}`;
}

/** True only for the exact same account generation, including its revision. */
export function isSameUsScope(a: UsScope, b: UsScope): boolean {
  return a.venue === b.venue && a.scopeKey === b.scopeKey && a.revision === b.revision;
}

// ── Order status ───────────────────────────────────────────────────────────

const TERMINAL: readonly string[] = ['filled', 'canceled', 'expired', 'rejected'] satisfies readonly UsLifecycleState[];

/**
 * Finished: a terminal venue lifecycle, or no lifecycle yet and a receipt saying
 * the venue refused the order (rejected) or it was never sent (aborted). A
 * lifecycle without a state counts as none. The rule of presentUsOrder's
 * `terminal`, for Trader Detail's manager read, whose rows carry a lifecycle
 * state and a submission state rather than a parsed UsOrderRow; portfolioModel.test
 * pins the two in step for every lifecycle state and receipt status.
 */
export function isTerminalUsOrder(lifecycleState: string | null | undefined, receiptStatus: string): boolean {
  return lifecycleState ? TERMINAL.includes(lifecycleState) : receiptStatus === 'rejected' || receiptStatus === 'aborted';
}

/** The venue lifecycle in words; shared with every surface that lists US orders. */
export const LIFECYCLE_LABEL: Record<UsLifecycleState, string> = {
  pending: 'Awaiting acceptance',
  open: 'Open',
  partial: 'Partially filled',
  filled: 'Filled',
  canceled: 'Canceled',
  expired: 'Expired',
  replaced: 'Replaced — reconciling',
  rejected: 'Rejected',
  conflict: 'Conflicting venue evidence',
};

export const RECEIPT_LABEL: Record<UsReceipt['status'], string> = {
  ready: 'Awaiting dispatch',
  aborted: 'Not sent — aborted',
  submitted: 'Submitted — acceptance unconfirmed',
  accepted: 'Accepted',
  rejected: 'Rejected',
  unknown: 'Result unknown',
};

/**
 * The one word a row marks itself with, beside its actions. A resting order is
 * unmarked, like a Kalshi/international row; amber says the order needs the
 * trader's eye (partly filled, not yet accepted, unconfirmed, conflicting),
 * muted names a finished or reconciling state. Null = no mark.
 */
export type UsStatusBadge = { text: string; tone: 'amber' | 'muted' };

export type UsOrderView = {
  key: string;
  status: string;
  /** Holdings progress never replaces a confirmed order lifecycle. */
  fillStatus: string | null;
  /** Why the status is uncertain or unfavourable, when the evidence says. */
  statusReason: string | null;
  /** The row's mark; `status` keeps the full words for other surfaces. */
  statusBadge: UsStatusBadge | null;
  terminal: boolean;
  ordered: string;
  filled: string;
  remaining: string;
  price: string;
  fees: string;
};

/** Exact USD text as dollars ("-$0.0025", "$1,783.00"); never rounded. */
function usd(value: string | null): string {
  return value === null ? UNAVAILABLE : formatUsUsd(value);
}

const amber = (text: string): UsStatusBadge => ({ text, tone: 'amber' });
const muted = (text: string): UsStatusBadge => ({ text, tone: 'muted' });

const LIFECYCLE_BADGE: Record<UsLifecycleState, UsStatusBadge | null> = {
  pending: amber('Pending'),
  open: null,
  partial: amber('Partial'),
  filled: muted('Filled'),
  canceled: muted('Canceled'),
  expired: muted('Expired'),
  replaced: muted('Replaced'),
  rejected: muted('Rejected'),
  conflict: amber('Conflict'),
};

const RECEIPT_BADGE: Record<UsReceipt['status'], UsStatusBadge | null> = {
  ready: amber('Pending'),
  aborted: muted('Not sent'),
  submitted: amber('Pending'),
  accepted: null,
  rejected: muted('Rejected'),
  unknown: amber('Unconfirmed'),
};

/**
 * The venue lifecycle outranks the receipt once it exists: a receipt records
 * what the submission call returned, the lifecycle what the venue later showed.
 */
export function presentUsOrder(row: UsOrderRow): UsOrderView {
  const life = row.lifecycle;
  const receipt = row.receipt;
  return {
    key: usOrderKey(row),
    status: life ? LIFECYCLE_LABEL[life.state] : RECEIPT_LABEL[receipt.status],
    fillStatus: usOrderNeedsFillVisibility(row) ? (row.fillProgress?.state === 'clearing' ? 'Fill clearing' : 'Holding unconfirmed')
      : row.fillProgress?.state === 'reversed' ? 'Fill reversed' : null,
    statusReason: !life && (receipt.status === 'unknown' || receipt.status === 'rejected' || receipt.status === 'aborted') ? receipt.reason : null,
    statusBadge: usOrderNeedsFillVisibility(row) ? amber(row.fillProgress?.state === 'clearing' ? 'Clearing' : 'Unconfirmed')
      : row.fillProgress?.state === 'reversed' ? muted('Reversed') : life ? LIFECYCLE_BADGE[life.state] : RECEIPT_BADGE[receipt.status],
    terminal: life ? TERMINAL.includes(life.state) : receipt.status === 'rejected' || receipt.status === 'aborted',
    ordered: row.quantity,
    filled: life ? life.filledQuantity : UNAVAILABLE,
    remaining: life?.remainingQuantity ?? UNAVAILABLE,
    price: row.outcomePrice,
    fees: usd(row.feesUsd),
  };
}

export type UsPositionView = {
  key: string;
  held: string;
  /**
   * Contracts G1 has seen clear for this trader, shown only when the adapter
   * marks it `local_cleared_contribution`. Never a holding and never a
   * substitute for `held` (docs/260928PolymarketUsPortfolioConnection.md).
   */
  knownContribution: string | null;
  allocation: UsPositionRow['allocation'] | null;
  fees: string;
  /** Nothing is known to be missing from this holding. */
  complete: boolean;
  reasons: readonly string[];
};

export function presentUsPosition(row: UsPositionRow): UsPositionView {
  return {
    key: usPositionKey(row),
    held: row.heldQuantity ?? UNAVAILABLE,
    allocation: (row.quantityBasis === 'local_cleared_contribution' || row.quantityBasis === 'g1_ledger') ? row.allocation ?? null : null,
    knownContribution:
      (row.quantityBasis === 'local_cleared_contribution' || row.quantityBasis === 'g1_ledger') && row.knownContribution !== undefined
        ? row.knownContribution
        : null,
    fees: usd(row.feesUsd),
    complete: row.heldQuantity !== null && row.reasons.length === 0,
    reasons: row.reasons,
  };
}

// ── Cancel readiness ───────────────────────────────────────────────────────

export type UsCancelView = {
  /** What the last cancel attempt established. Shown even without cancel rights. */
  status: string | null;
  canCancel: boolean;
  canCheckStatus: boolean;
  canRepeatCancel: boolean;
  /** When a repeat cancel unlocks; null when none is pending. */
  repeatAvailableAtMs: number | null;
  /** Why Cancel is not offered, when it is not. */
  blockedReason: string | null;
};

const CANCEL_STATUS = {
  dispatching: 'Cancel pending',
  acknowledged: 'Cancel requested — awaiting confirmation',
  unknown: 'Cancel unconfirmed',
  not_sent: "Cancel didn't go out",
} as const;

function cancelPermission(row: UsOrderRow, readiness: UsReadiness): string | null {
  if (readiness.state === 'unavailable') return `Account unavailable: ${usReason(readiness.reason)}`;
  if (row.ownership === 'own' ? readiness.canCancelOwn : readiness.canCancelDesk) return null;
  return row.ownership === 'own' ? 'You do not have permission to cancel your orders' : "You can view this desk order but can't cancel it";
}

/**
 * Follows the relay's cancel rules (relay/src/polymarketUs/cancellation.ts):
 * - A first cancel needs an exact venue order id and the matching own/desk
 *   right (`claim`, US_KNOWN_ORDER_REQUIRED).
 * - The active attempt is the latest one that was actually sent. While one
 *   exists, a plain cancel is a no-op on the server, so it is not offered.
 * - For a sent attempt (`dispatching`, `acknowledged` or `unknown`), "check
 *   status" is a read and is offered at once. A repeat cancel is offered only
 *   after the quiet interval, and only while the order is open or partial —
 *   the relay checks nothing else about the earlier attempt's outcome.
 * - A `not_sent` attempt with nothing sent before it offers Cancel again.
 * Only the order lifecycle can say an order is canceled.
 */
export function usCancelView(row: UsOrderRow, readiness: UsReadiness, nowMs: number): UsCancelView {
  const attempt = row.cancel;
  const sent = attempt !== null && attempt.state !== 'not_sent';
  const life = row.lifecycle;
  const terminal = life ? TERMINAL.includes(life.state) : row.receipt.status === 'rejected' || row.receipt.status === 'aborted';
  const denied = cancelPermission(row, readiness);

  let status: string | null = attempt ? CANCEL_STATUS[attempt.state] : null;
  if (terminal) status = sent && life?.state === 'filled' ? 'Filled before the cancel took effect' : null;

  const none = (blockedReason: string | null): UsCancelView => ({
    status,
    canCancel: false,
    // The row was obtained through view authority; the GET rechecks that
    // authority. Cancel rights, an execution key, and a venue ID are not needed.
    canCheckStatus: true,
    canRepeatCancel: false,
    repeatAvailableAtMs: null,
    blockedReason,
  });

  if (terminal) return none(null);
  if (!row.orderId) {
    return none(
      row.receipt.status === 'ready'
        ? 'Not sent to the venue yet — nothing to cancel'
        : 'No venue order id — the result must be reconciled first',
    );
  }
  if (denied) return none(denied);
  if (!sent) return { ...none(null), canCancel: true };

  const repeatAvailableAtMs = attempt.requestedAtMs + US_CANCEL_REPEAT_WAIT_MS;
  const repeatableState = life?.state === 'open' || life?.state === 'partial';
  return {
    ...none(null),
    canCheckStatus: true,
    canRepeatCancel: repeatableState && nowMs >= repeatAvailableAtMs,
    repeatAvailableAtMs: repeatableState ? repeatAvailableAtMs : null,
  };
}

// ── Applying late results ──────────────────────────────────────────────────
//
// The presenter never re-decides which order evidence is newer. The relay's
// projection is monotonic (orderLifecycle.ts: fills never decrease, late
// pending/open evidence cannot erase acceptance or a terminal state), and a
// local read time is not an exchange version (docs/260927PolymarketUsReconciliation.md).
// What the presenter guards is attribution: a result lands only on the exact
// scope and row it was requested for. Dropping a response that was overtaken
// by a later request is the loader's job.

/**
 * Accept a portfolio read only for the exact scope currently shown. A read for
 * account A that lands after the view switched to B is dropped, not painted,
 * and so is a read carrying any row from another scope.
 */
export function acceptUsSnapshot(current: UsScope, incoming: UsPortfolioSnapshot): boolean {
  return isSameUsScope(current, incoming.scope)
    && incoming.orders.every((o) => isSameUsScope(current, o.scope))
    && incoming.positions.every((p) => isSameUsScope(current, p.scope));
}

/**
 * The cancel attempt to show, mirroring the relay's "active attempt is the
 * highest non-unsent generation": a proven-unsent repeat never hides the sent
 * attempt before it, a late outcome for an earlier attempt never replaces a
 * later one, and a settled outcome never reverts to `dispatching`.
 */
function activeCancelAttempt(existing: UsCancelAttempt | null, incoming: UsCancelAttempt): UsCancelAttempt {
  if (!existing) return incoming;
  if (existing.attemptId === incoming.attemptId) {
    return existing.state !== 'dispatching' && incoming.state === 'dispatching' ? existing : incoming;
  }
  // A sent attempt always outranks a proven-unsent one, whichever is newer:
  // after a reload the row may hold only the unsent successor's receipt
  // (docs/260927PolymarketUsCancellation.md), and the relay answers with the
  // sent attempt it still treats as active.
  const existingSent = existing.state !== 'not_sent';
  const incomingSent = incoming.state !== 'not_sent';
  if (existingSent !== incomingSent) return incomingSent ? incoming : existing;
  if (existing.generation !== undefined && incoming.generation !== undefined) {
    return incoming.generation > existing.generation ? incoming : existing;
  }
  // Fixture-only legacy shapes may lack a generation. Connected rows always
  // carry one; a missing one cannot supersede server-ordered evidence.
  if (existing.generation !== undefined) return existing;
  if (incoming.generation !== undefined) return incoming;
  return incoming.requestedAtMs >= existing.requestedAtMs ? incoming : existing;
}

function retainNewTerminalFill(row: UsOrderRow): UsOrderRow {
  // A status/cancel read has no holdings snapshot. Keep a newly finished fill
  // visible until the portfolio read supplies that evidence; receipts elsewhere
  // keep their lifecycle wording and are never treated as portfolio reads.
  if (row.fillProgress || !isTerminalUsOrder(row.lifecycle?.state, row.receipt.status)) return row;
  const progress = usFillProgress(row, [], false);
  return progress ? { ...row, fillProgress: progress } : row;
}

/**
 * Apply a cancel result to the snapshot it was requested from. Returns the
 * same snapshot object when the result belongs elsewhere, so a caller can
 * tell nothing changed. An order in the result replaces the row with the
 * relay's current projection; the row is never removed here.
 */
export function applyUsCancelResult(snapshot: UsPortfolioSnapshot, result: UsCancelResult): UsPortfolioSnapshot {
  if (!isSameUsScope(result.scope, snapshot.scope)) return snapshot;
  const index = snapshot.orders.findIndex((o) => o.intentId === result.intentId);
  if (index < 0) return snapshot;
  const existing = snapshot.orders[index];
  const cancel = activeCancelAttempt(existing.cancel, result.attempt);
  const readback = result.order && isSameUsScope(result.order.scope, snapshot.scope)
    && usOrderKey(result.order) === usOrderKey(existing) ? result.order : existing;
  const orders = snapshot.orders.slice();
  orders[index] = { ...retainNewTerminalFill(readback), cancel };
  return { ...snapshot, orders };
}

/**
 * Apply a "check status" answer (the exact order, freshly read) to the
 * snapshot it was requested from. Same attribution rules as a cancel result:
 * exact scope including revision, exact row. The cancel attempt shown is the
 * relay's active one, so an answer without a cancel attempt never erases the
 * one already shown. Returns the same snapshot object when nothing applies.
 */
export function applyUsOrderStatus(snapshot: UsPortfolioSnapshot, order: UsOrderRow): UsPortfolioSnapshot {
  if (!isSameUsScope(order.scope, snapshot.scope)) return snapshot;
  const key = usOrderKey(order);
  const index = snapshot.orders.findIndex((o) => usOrderKey(o) === key);
  if (index < 0) return snapshot;
  const existing = snapshot.orders[index];
  const cancel = order.cancel
    ? activeCancelAttempt(existing.cancel, order.cancel)
    : existing.cancel;
  const orders = snapshot.orders.slice();
  orders[index] = { ...retainNewTerminalFill(order), cancel };
  return { ...snapshot, orders };
}
